// JobsPage.jsx — the Jobs page (Braeden 8/10).
//
//   "jobs need to have a thing where you can make a new job, by typing in all the
//    info needed for the job, than below should show you all of the jobs in a
//    list, and the top should be another tracker for managment, all trackers need
//    this style, get rid of the colors just make it plain grey"
//
// Top: management tracker (same bubble language as Leads, flat grey — no colors).
// Middle: new-job form, every field needed to actually book the work.
// Bottom: every job in a list.
//
// Named JobsPage, not JobsView — App.jsx already imports a different JobsView.
//
// Backend is services/bizOps/jobs.js via routes/bizOps.js. Note BIZOPS_DRY_RUN
// is still 1, so creating a job here files it without texting the customer.
//
// See ARIA_COMPANY_TABS_SPEC.md.

import React from 'react';

const BLANK = {
  customer: '', phone: '', address: '', service: '',
  date: '', time: '', price: '', notes: '',
};

// The same stable id HomeScreen resolves to. If these two ever disagree the
// desktop tab and the phone show different jobs and neither looks broken.
function ownerId() {
  try { return localStorage.getItem('howto_owner_id') || 'owner-default'; }
  catch (_) { return 'owner-default'; }
}

const STATUS_LABEL = { scheduled: 'Scheduled', set: 'Set', confirmed: 'Confirmed', done: 'Done', canceled: 'Canceled' };

export default function JobsPage() {
  const [jobs, setJobs] = React.useState([]);
  const [state, setState] = React.useState('loading'); // loading | ready | error
  const [form, setForm] = React.useState(BLANK);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null); // {kind:'ok'|'err', text}
  const [adding, setAdding] = React.useState(false);        // the popup, closed by default
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [leads, setLeads] = React.useState([]);
  const [leadState, setLeadState] = React.useState('loading');
  const [scheduling, setScheduling] = React.useState(null);   // the lead being placed
  const [calUrl, setCalUrl] = React.useState(null);
  const [openJob, setOpenJob] = React.useState(null);   // the job you clicked into

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), 9000);
      const r = await fetch(`/api/bizops/jobs?ownerId=${encodeURIComponent(ownerId())}`, { signal: ctl.signal });
      clearTimeout(t);
      if (!r.ok) throw new Error(String(r.status));
      const j = await r.json();
      setJobs(Array.isArray(j) ? j : (j.jobs || []));
      setState('ready');
    } catch (_) {
      setJobs([]);
      setState('error');
    }
  }, []);

  // The real inbound leads. /api/leads/list reads services/leadLedger, the
  // same ledger the summary counts come from — no second source of truth.
  const loadLeads = React.useCallback(async () => {
    setLeadState('loading');
    try {
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), 9000);
      const r = await fetch('/api/leads/list', { signal: ctl.signal });
      clearTimeout(t);
      if (!r.ok) throw new Error(String(r.status));
      const j = await r.json();
      setLeads(Array.isArray(j.leads) ? j.leads : []);
      setLeadState('ready');
    } catch (_) { setLeads([]); setLeadState('error'); }
  }, []);

  React.useEffect(() => { load(); loadLeads(); }, [load, loadLeads]);

  // Fetch the owner's real Google Calendar embed only when a lead is opened.
  React.useEffect(() => {
    if (!scheduling) { setCalUrl(null); return; }
    let alive = true;
    setCalUrl('loading');
    fetch('/api/schedule?ownerId=owner-default')
      .then((r) => r.json())
      .then((d) => { if (alive) setCalUrl(d && d.embedUrl ? d.embedUrl : 'none'); })
      .catch(() => { if (alive) setCalUrl('none'); });
    return () => { alive = false; };
  }, [scheduling]);

  // Carry the lead's details into the New job form so nothing is retyped.
  const prefillFromLead = (l) => setForm({
    ...BLANK,
    customer: l.name || '', phone: l.phone || '',
    service: l.details || '', notes: l.source ? `From ${l.source}.` : '',
  });

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setMsg(null); };

  // Anything typed = worth asking about before it disappears.
  const dirty = Object.values(form).some((v) => String(v).trim() !== '');
  const openAdd = () => { setMsg(null); setConfirmDiscard(false); setAdding(true); };
  const closeAdd = () => { setAdding(false); setConfirmDiscard(false); };
  const discard = () => { setForm(BLANK); closeAdd(); };
  // Backdrop, ×, Cancel and Escape all route through here so there is exactly
  // ONE rule about losing work, not four slightly different ones.
  const tryClose = () => { if (saving) return; if (dirty) setConfirmDiscard(true); else closeAdd(); };

  // Escape closes; the page behind must not scroll under an open popup.
  React.useEffect(() => {
    if (!adding) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); tryClose(); } };
    window.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey, true); document.body.style.overflow = prev; };
  });

  const canSave = form.customer.trim() && form.service.trim() && form.date;

  async function submit(e) {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    setMsg(null);
    try {
      const r = await fetch('/api/bizops/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerId: ownerId(),   // required by services/bizOps/jobs.createJob
          customerName: form.customer.trim(),   // createJob destructures customerName
          phone: form.phone.trim(),
          address: form.address.trim(),
          service: form.service.trim(),
          window: form.time || '9:00 AM',      // arrival window, not the date
          date: form.date,
          time: form.time,
          price: form.price ? Number(form.price) : undefined,
          notes: form.notes.trim(),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setForm(BLANK);
      setMsg({ kind: 'ok', text: 'Job booked.' });
      closeAdd();   // saved - drop the popup so the new job is visible in the list
      load();
    } catch (err) {
      setMsg({ kind: 'err', text: `Could not save the job — ${err.message}` });
    } finally {
      setSaving(false);
    }
  }

  // Move a job along. Best-effort: if the backend has no status route the
  // popup says so instead of pretending the click worked.
  async function setStatus(job, status) {
    if (saving) return;
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`/api/bizops/jobs/${encodeURIComponent(job.id)}/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId: ownerId(), status }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setOpenJob({ ...job, status });
      setMsg({ kind: 'ok', text: 'Updated.' });
      load();
    } catch (err) {
      setMsg({ kind: 'err', text: `Could not update — ${err.message}` });
    } finally { setSaving(false); }
  }

  // Management tracker numbers, straight off the list — no invented figures.
  const now = new Date();
  const isSameDay = (d) => {
    if (!d) return false;
    const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const x = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(String(d).replace(' ', 'T'));
    return !isNaN(x) && x.toDateString() === now.toDateString();
  };
  const open = jobs.filter((j) => j.status !== 'done' && j.status !== 'canceled');
  const today = jobs.filter((j) => isSameDay(j.date || j.window));
  const done = jobs.filter((j) => j.status === 'done');
  const booked = jobs.reduce((n, j) => n + (Number(j.price) || 0), 0);

  return (
    <div className="jp">
      <style>{CSS}</style>

      {/* ── TRACKER ─────────────────────────────────────────────────
          Braeden 8/11: plain colour, and the "+ Add a job" lives top-right.
          The form is no longer always open — it sits behind that button, so
          the page reads as the JOB LIST first and the form only when asked. */}
      <div className="jp-hero">
        <div className="jp-hero__top">
          <div>
            <div className="jp-hero__label">Jobs · all time</div>
            <div className="jp-hero__value">{state === 'loading' ? '—' : jobs.length.toLocaleString()}</div>
          </div>
          <button
            type="button"
            className={`jp-add${adding ? ' jp-add--on' : ''}`}
            onClick={() => (adding ? tryClose() : openAdd())}
            aria-expanded={adding}
          >
            <span className="jp-add__plus">{adding ? '×' : '+'}</span>
            {adding ? 'Close' : 'New job'}
          </button>
        </div>
        <div className="jp-hero__foot">
          <span>{open.length} open</span>
          <span>{today.length} today</span>
          <span>{done.length} done</span>
          <span>${Math.round(booked).toLocaleString()} booked</span>
        </div>
      </div>

      {/* ── NEW JOB — a real popup over the page ────────────────────
          Braeden 8/11: "if they click add a job a pop up needs to come up,
          so if they add it at the end it will save or they can have a pop up
          that says discard if they change their mind."
          Closing with anything typed asks first — nobody loses a half-filled
          job to a stray click on the backdrop or the Escape key. */}
      {adding && (
      <div className="jp-modal" role="dialog" aria-modal="true" aria-label="Add a job">
        <div className="jp-modal__back" onClick={tryClose} />
        <form className="jp-new jp-new--modal" onSubmit={submit}>
        <div className="jp-new__head">
          <div className="jp-new__title">New job</div>
          <button type="button" className="jp-x" onClick={tryClose} aria-label="Close">×</button>
        </div>

        <div className="jp-fields">
          <Field label="Customer" required value={form.customer} onChange={set('customer')} placeholder="Mike Sanders" />
          <Field label="Phone" value={form.phone} onChange={set('phone')} placeholder="(316) 555-0142" type="tel" />
          <Field label="Address" wide value={form.address} onChange={set('address')} placeholder="1420 N Elm St, Wichita" />
          <Field label="Service" required value={form.service} onChange={set('service')} placeholder="Water heater replacement" />
          <Field label="Date" required value={form.date} onChange={set('date')} type="date" />
          <Field label="Time" value={form.time} onChange={set('time')} type="time" />
          <Field label="Price" value={form.price} onChange={set('price')} placeholder="450" type="number" prefix="$" />
          <Field label="Notes" wide textarea value={form.notes} onChange={set('notes')} placeholder="Gate code 4412. Dog in the yard." />
        </div>

        <div className="jp-new__row">
          <button type="submit" className="jp-btn" disabled={!canSave || saving}>
            {saving ? 'Saving…' : 'Save the job'}
          </button>
          <button type="button" className="jp-btn jp-btn--ghost" onClick={tryClose} disabled={saving}>
            Cancel
          </button>
          {!canSave && <span className="jp-hint">Customer, service and date are required.</span>}
          {msg && <span className={`jp-msg jp-msg--${msg.kind}`}>{msg.text}</span>}
        </div>
        </form>

        {/* the change-of-mind popup — only ever shown over a filled-in form */}
        {confirmDiscard && (
          <div className="jp-confirm" role="alertdialog" aria-label="Discard this job?">
            <div className="jp-confirm__card">
              <div className="jp-confirm__title">Discard this job?</div>
              <div className="jp-confirm__body">What you typed will be thrown away.</div>
              <div className="jp-confirm__row">
                <button type="button" className="jp-btn jp-btn--ghost" onClick={() => setConfirmDiscard(false)}>
                  Keep editing
                </button>
                <button type="button" className="jp-btn jp-btn--danger" onClick={discard}>
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* ── LEADS ───────────────────────────────────────────────────
          Braeden 8/11: "below needs to be a title that says leads, so when a
          lead comes through and it needs to be scheduled, they can click it
          and schedule it, when they do the real google calendar pops up and
          they can add it where they want on the schedule."
          Clicking a lead opens the owner's REAL Google Calendar, so the slot
          is chosen against the week he can actually see. */}
      <div className="jp-listwrap">
        <div className="jp-listhead">
          <span>Leads</span>
          <button type="button" className="jp-refresh" onClick={loadLeads}>Refresh</button>
        </div>

        {leadState === 'loading' && <div className="jp-empty">Loading…</div>}
        {leadState === 'error' && <div className="jp-empty">Couldn&rsquo;t reach the leads service.</div>}
        {leadState === 'ready' && leads.length === 0 && (
          <div className="jp-empty">No leads yet. They land here the moment one comes in.</div>
        )}

        {leadState === 'ready' && leads.length > 0 && (
          <div className="jp-list">
            {leads.map((l) => (
              <button type="button" className="jp-lead" key={l.id} onClick={() => setScheduling(l)}>
                <span className="jp-lead__who">
                  <b>{l.name || 'No name'}</b>
                  <i>{l.details || l.source}</i>
                </span>
                <span className="jp-lead__meta">{l.phone || l.email || ''}</span>
                <span className="jp-lead__when">{fmtDate(l.ts)}</span>
                <span className={`jp-tag jp-tag--${l.scheduled ? 'done' : 'set'}`}>
                  {l.scheduled ? 'Scheduled' : 'Schedule ›'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* the real Google Calendar, so the slot is picked against the real week */}
      {scheduling && (
        <div className="jp-modal" role="dialog" aria-modal="true" aria-label="Schedule this lead">
          <div className="jp-modal__back" onClick={() => setScheduling(null)} />
          <div className="jp-new jp-new--modal jp-cal">
            <div className="jp-new__head">
              <div>
                <div className="jp-new__title">Schedule {scheduling.name || 'this lead'}</div>
                <div className="jp-cal__sub">
                  {scheduling.details || scheduling.source}
                  {scheduling.phone ? ` · ${scheduling.phone}` : ''}
                </div>
              </div>
              <button type="button" className="jp-x" onClick={() => setScheduling(null)} aria-label="Close">×</button>
            </div>

            {calUrl === 'loading' && <div className="jp-empty">Opening your calendar…</div>}
            {calUrl === 'none' && (
              <div className="jp-empty">
                No Google Calendar is connected yet, so there is nothing to drop this into.
              </div>
            )}
            {calUrl && calUrl !== 'loading' && calUrl !== 'none' && (
              <iframe className="jp-cal__frame" src={calUrl} title="Google Calendar" />
            )}

            <div className="jp-new__row">
              <button
                type="button"
                className="jp-btn"
                onClick={() => { prefillFromLead(scheduling); setScheduling(null); openAdd(); }}
              >
                Book it as a job
              </button>
              <button type="button" className="jp-btn jp-btn--ghost" onClick={() => setScheduling(null)}>Close</button>
              <span className="jp-hint">Add it straight on the calendar, or book it as a job here.</span>
            </div>
          </div>
        </div>
      )}

      {/* ── ONE JOB, opened ─────────────────────────────────────────
          Braeden 8/11: "i need to be able to click into it." A list you
          cannot open is just a receipt — every field that was captured shows
          here, and the status can be moved without leaving the page. */}
      {openJob && (
        <div className="jp-modal" role="dialog" aria-modal="true" aria-label="Job detail">
          <div className="jp-modal__back" onClick={() => setOpenJob(null)} />
          <div className="jp-new jp-new--modal">
            <div className="jp-new__head">
              <div>
                <div className="jp-new__title">{openJob.customerName || openJob.customer || 'Job'}</div>
                <div className="jp-cal__sub">{openJob.service || ''}</div>
              </div>
              <button type="button" className="jp-x" onClick={() => setOpenJob(null)} aria-label="Close">×</button>
            </div>

            <div className="jp-detail">
              <Row label="When" value={[fmtDate(openJob.date), openJob.window].filter(Boolean).join(' · ')} />
              <Row label="Phone" value={openJob.phone} />
              <Row label="Address" value={openJob.address} />
              <Row label="Price" value={openJob.price ? `$${Math.round(Number(openJob.price)).toLocaleString()}` : ''} />
              <Row label="Status" value={STATUS_LABEL[openJob.status] || 'Set'} />
              <Row label="Notes" value={openJob.notes} />
            </div>

            <div className="jp-new__row">
              {['confirmed', 'done', 'canceled'].map((st) => (
                <button
                  key={st}
                  type="button"
                  className={`jp-btn${st === 'canceled' ? ' jp-btn--danger' : st === 'done' ? '' : ' jp-btn--ghost'}`}
                  disabled={saving || openJob.status === st}
                  onClick={() => setStatus(openJob, st)}
                >
                  {st === 'confirmed' ? 'Mark confirmed' : st === 'done' ? 'Mark done' : 'Cancel job'}
                </button>
              ))}
              {msg && <span className={`jp-msg jp-msg--${msg.kind}`}>{msg.text}</span>}
            </div>
          </div>
        </div>
      )}

      {/* ── ALL JOBS ────────────────────────────────────────────── */}
      <div className="jp-listwrap">
        <div className="jp-listhead">
          <span>All jobs</span>
          <button type="button" className="jp-refresh" onClick={load}>Refresh</button>
        </div>

        {state === 'loading' && <div className="jp-empty">Loading…</div>}

        {state === 'error' && (
          <div className="jp-empty">
            Couldn't reach the jobs service. Nothing was lost — the list just isn't loading.
          </div>
        )}

        {state === 'ready' && jobs.length === 0 && (
          <div className="jp-empty">No jobs yet. Tap “+ Add a job” and it shows up here.</div>
        )}

        {state === 'ready' && jobs.length > 0 && (
          <div className="jp-list">
            {jobs.map((j, i) => (
              <button type="button" className="jp-job" key={j.id || i} onClick={() => setOpenJob(j)}>
                <span className="jp-job__when">
                  <b>{fmtDate(j.date)}</b>
                  <i>{j.window || ''}</i>
                </span>
                <span className="jp-job__who">
                  <b>{j.customerName || j.customer || 'Customer'}</b>
                  <i>{j.service || ''}</i>
                </span>
                <span className="jp-job__where">{j.address || ''}</span>
                <span className="jp-job__price">{j.price ? `$${Math.round(Number(j.price)).toLocaleString()}` : ''}</span>
                <span className={`jp-tag jp-tag--${j.status || 'set'}`}>{STATUS_LABEL[j.status] || 'Set'}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', required, wide, textarea, prefix }) {
  return (
    <label className={`jp-f${wide ? ' jp-f--wide' : ''}`}>
      <span className="jp-f__label">{label}{required && <i>*</i>}</span>
      {textarea ? (
        <textarea className="jp-f__in" rows={2} value={value} onChange={onChange} placeholder={placeholder} />
      ) : (
        <span className="jp-f__wrap">
          {prefix && <span className="jp-f__prefix">{prefix}</span>}
          <input className="jp-f__in" type={type} value={value} onChange={onChange} placeholder={placeholder} />
        </span>
      )}
    </label>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  const str = String(d).trim();
  // A bare YYYY-MM-DD is a CALENDAR date, not an instant. new Date('2026-08-14')
  // parses as UTC midnight and prints "Aug 13" anywhere west of Greenwich —
  // a job booked for Friday showed as Thursday.
  const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const x = ymd ? new Date(+ymd[1], +ymd[2] - 1, +ymd[3]) : new Date(str.replace(' ', 'T'));
  if (isNaN(x)) return str;
  return x.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="jp-row">
      <span className="jp-row__k">{label}</span>
      <span className="jp-row__v">{value}</span>
    </div>
  );
}

const CSS = `
/* clicked-into job */
.jp-detail { display:flex; flex-direction:column; gap:0; border:1px solid #eef1f5; border-radius:12px; overflow:hidden; }
.jp-row { display:grid; grid-template-columns:120px 1fr; gap:12px; padding:12px 16px; border-bottom:1px solid #eef1f5; }
.jp-row:last-child { border-bottom:0; }
.jp-row__k { color:#8a94a6; font-weight:700; font-size:13.5px; }
.jp-row__v { color:#0b1020; font-size:15px; }
.jp-job { width:100%; text-align:left; cursor:pointer; font:inherit; border:0; border-bottom:1px solid #eef1f5; }
.jp-job:hover { background:#f6f8fb; }

/* ── leads ── */
.jp-lead { display:grid; grid-template-columns:1fr auto auto auto; gap:14px; align-items:center;
  width:100%; text-align:left; cursor:pointer; font:inherit;
  border:0; border-bottom:1px solid #eef1f5; background:#fff; padding:14px 18px; }
.jp-lead:last-child { border-bottom:0; }
.jp-lead:hover { background:#f6f8fb; }
.jp-lead__who { display:flex; flex-direction:column; gap:2px; min-width:0; }
.jp-lead__who b { font-weight:800; color:#0b1020; font-size:15px; }
.jp-lead__who i { font-style:normal; color:#5b6472; font-size:13.5px;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.jp-lead__meta { color:#5b6472; font-size:14px; }
.jp-lead__when { color:#8a94a6; font-size:13.5px; }

/* the calendar popup */
.jp-cal { width:min(1100px,100%); }
.jp-cal__sub { color:#5b6472; font-size:14px; margin-top:4px; }
.jp-cal__frame { width:100%; height:min(62vh,620px); border:1px solid #e2e7ee; border-radius:12px; background:#fff; }

/* ── the Add-a-job popup ─────────────────────────────────────────────
   A real dialog over the page, not an inline panel: the job list stays
   put behind it, and nothing you typed vanishes without being asked. */
.jp-modal { position:fixed; inset:0; z-index:1200; display:flex;
  align-items:flex-start; justify-content:center; padding:min(6vh,60px) 16px 40px; overflow-y:auto; }
.jp-modal__back { position:fixed; inset:0; background:rgba(8,12,20,.55); backdrop-filter:blur(2px); }
.jp-new--modal { position:relative; z-index:1; width:min(760px,100%); margin:0;
  box-shadow:0 24px 60px rgba(8,12,20,.34); animation:jpIn .18s ease-out; }
@keyframes jpIn { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:none } }
.jp-new__head { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:14px; }
.jp-new__head .jp-new__title { margin-bottom:0; }
.jp-x { border:0; background:transparent; cursor:pointer; font-size:26px; line-height:1;
  color:#8a94a6; padding:0 4px; border-radius:8px; }
.jp-x:hover { color:#0b1020; background:#f1f4f8; }
.jp-btn--ghost { background:#f1f4f8; color:#0b1020; }
.jp-btn--danger { background:#c62828; color:#fff; }

/* change-of-mind popup, over the form */
.jp-confirm { position:absolute; inset:0; z-index:2; display:flex; align-items:center; justify-content:center;
  background:rgba(8,12,20,.42); padding:16px; }
.jp-confirm__card { background:#fff; border-radius:16px; padding:22px 24px; width:min(400px,100%);
  box-shadow:0 20px 50px rgba(8,12,20,.4); }
.jp-confirm__title { font-weight:800; font-size:19px; color:#0b1020; }
.jp-confirm__body { color:#5b6472; font-size:15px; margin:8px 0 18px; }
.jp-confirm__row { display:flex; gap:10px; justify-content:flex-end; }

.jp { padding:26px clamp(14px,3vw,34px) 60px; max-width:1180px; margin:0 auto; }

/* tracker — PLAIN. No gradient, no gold. Braeden 8/11: "a plane color". */
.jp-hero { border-radius:14px; padding:18px 22px 20px; color:#0b1020;
  background:#eef1f5; border:1px solid #e2e7ee; box-shadow:none; }
.jp-hero__top { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; }
.jp-hero__label { font-weight:800; font-size:13px; color:#5b6472; }

/* the + lives top-right and opens the form */
.jp-add { display:inline-flex; align-items:center; gap:8px; cursor:pointer;
  border:1px solid #d5dbe4; border-radius:11px; padding:10px 16px;
  background:#fff; color:#0b1020; font:inherit; font-weight:800; font-size:15px;
  transition:background .18s ease, border-color .18s ease; }
.jp-add:hover { background:#f6f8fb; border-color:#c3ccd8; }
.jp-add__plus { font-size:19px; line-height:1; font-weight:700; }
.jp-add--on { background:#0b1020; color:#fff; border-color:#0b1020; }
.jp-hero__value { font-weight:800; letter-spacing:-.03em; line-height:1;
  font-size:clamp(36px,5vw,58px); margin:10px 0 12px; }
.jp-hero__foot { display:flex; gap:22px; flex-wrap:wrap; font-size:14px; font-weight:600; opacity:.88; }

/* new job */
.jp-new { margin-top:16px; background:#fff; border-radius:20px; padding:22px clamp(18px,2.6vw,30px);
  box-shadow:0 1px 2px rgba(16,32,64,.06), 0 6px 18px rgba(16,32,64,.05); }
.jp-new__title { font-weight:800; letter-spacing:-.03em; font-size:26px; color:#0b1020; margin-bottom:14px; }
.jp-fields { display:grid; gap:13px; grid-template-columns:repeat(auto-fit, minmax(210px,1fr)); }
.jp-f { display:flex; flex-direction:column; gap:5px; min-width:0; }
.jp-f--wide { grid-column:1 / -1; }
.jp-f__label { font-weight:700; font-size:13px; color:#5b6472; }
.jp-f__label i { color:#d13b3b; font-style:normal; margin-left:2px; }
.jp-f__wrap { display:flex; align-items:center; gap:0; }
.jp-f__prefix { padding:0 4px 0 12px; color:#8a94a6; font-weight:700;
  border:1px solid #dfe4ec; border-right:0; border-radius:11px 0 0 11px; background:#fbfcfe;
  align-self:stretch; display:flex; align-items:center; }
.jp-f__prefix + .jp-f__in { border-radius:0 11px 11px 0; }
.jp-f__in { width:100%; box-sizing:border-box; border:1px solid #dfe4ec; border-radius:11px;
  padding:10px 12px; font:inherit; font-size:15px; color:#0b1020; background:#fbfcfe; resize:vertical; }
.jp-f__in:focus { outline:2px solid #1668ff; outline-offset:1px; border-color:transparent; }
.jp-new__row { display:flex; align-items:center; gap:14px; flex-wrap:wrap; margin-top:16px; }
.jp-btn { border:0; border-radius:11px; padding:12px 22px; cursor:pointer;
  font-weight:800; font-size:15px; background:#0b1020; color:#fff; }
.jp-btn:disabled { opacity:.38; cursor:default; }
.jp-hint { font-size:14px; color:#8a94a6; }
.jp-msg { font-size:14px; font-weight:600; }
.jp-msg--ok { color:#16a34a; }
.jp-msg--err { color:#d13b3b; }

/* list */
.jp-listwrap { margin-top:16px; }
.jp-listhead { display:flex; align-items:center; justify-content:space-between;
  font-weight:800; letter-spacing:-.02em; font-size:20px; color:#0b1020; margin-bottom:11px; }
.jp-refresh { border:0; background:none; cursor:pointer; font-weight:700; font-size:14px; color:#1668ff; }
.jp-list { display:flex; flex-direction:column; gap:10px; }
.jp-job { display:grid; align-items:center; gap:clamp(10px,2vw,26px);
  grid-template-columns:96px minmax(0,1.4fr) minmax(0,1.2fr) auto auto;
  background:#fff; border-radius:14px; padding:14px clamp(14px,2vw,22px);
  box-shadow:0 1px 2px rgba(16,32,64,.06), 0 4px 14px rgba(16,32,64,.04); }
.jp-job__when, .jp-job__who { display:flex; flex-direction:column; min-width:0; }
.jp-job b { font-weight:800; letter-spacing:-.02em; font-size:15px; color:#0b1020; }
.jp-job i { font-style:normal; font-size:13px; color:#5b6472; }
.jp-job__where { font-size:14px; color:#5b6472; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.jp-job__price { font-weight:800; font-size:16px; color:#0b1020; }
.jp-tag { font-size:12px; font-weight:800; letter-spacing:.04em; text-transform:uppercase;
  padding:5px 10px; border-radius:999px; background:#eef1f6; color:#5b6472; white-space:nowrap; }
.jp-tag--confirmed { background:#e6f6ec; color:#16a34a; }
.jp-tag--done { background:#eef1f6; color:#6b7280; }
.jp-tag--canceled { background:#fdeaea; color:#d13b3b; }
.jp-empty { background:#fff; border-radius:14px; padding:26px; text-align:center;
  color:#8a94a6; font-weight:600; box-shadow:0 1px 2px rgba(16,32,64,.06); }

@media (max-width:760px) {
  .jp-job { grid-template-columns:1fr auto; gap:6px 12px; }
  .jp-job__where { grid-column:1 / -1; white-space:normal; }
}
`;
